import { QueryKey, useQueries } from '@tanstack/react-query'
import { chunk } from 'lodash'
import { useCallback, useMemo } from 'react'
import { OpalApiRequestParams, OpalApiResponseDocument } from '../schema'

const MAX_FILTER_LENGTH = 100

type ParallelOperations = 'ReadBlocksV3'

type SuccessfulCollectionResponse<TOperationId extends ParallelOperations> =
  OpalApiResponseDocument<TOperationId, 200>

type FilterList<TOperationId extends ParallelOperations> =
  OpalApiRequestParams<TOperationId>['query']['filter']

type UseParallelFilteredQueriesArgs<
  TOperationId extends ParallelOperations,
  TSelectedData,
> = {
  enabled?: boolean
  /**
   * Array of uuids that are being filtered. Arrays longer than
   * 100 items will be broken into multiple requests.
   */
  filterList: string[]
  filterKey: keyof FilterList<TOperationId>
  /**
   * resource queryKey, omitting any request params, which
   * will be supplied here to include chunked filters
   */
  queryKeyWithoutRequestParams: QueryKey
  /**
   * The `readCollection` function that makes the API request.
   * E.g. `requestFn: moments.readCollection`
   */
  requestFn: ({
    params,
  }: {
    params: OpalApiRequestParams<TOperationId>
  }) => Promise<SuccessfulCollectionResponse<TOperationId>>
  /**
   * Other request params. Can include other filters that do not
   * need thier length managed.
   */
  requestParams: OpalApiRequestParams<TOperationId>
  select: (data?: SuccessfulCollectionResponse<TOperationId>) => TSelectedData
}

/**
 * Used when filtering by object ids and there are more than 100 uuids in the
 * filter list. This breaks apart the filter list into multiple chunks that
 * are fetched in parallal so we don't run over the url character limit
 * (safe value ~2,000 characters). This only handles chunking by a single
 * filter list, but other filters can still be included in the request
 * params. White list appropriate operations in `ParallelOperations` when
 * necessary.
 * @returns Array of post-selection request data
 */
export const useParallelFilteredQueries = <
  TOperationId extends ParallelOperations,
  TSelectedData,
>({
  enabled,
  filterList,
  filterKey,
  queryKeyWithoutRequestParams,
  requestFn,
  requestParams,
  select,
}: UseParallelFilteredQueriesArgs<TOperationId, TSelectedData>) => {
  const filterLists = useMemo(() => {
    if (filterList.length <= MAX_FILTER_LENGTH) return [filterList]

    return chunk(filterList, MAX_FILTER_LENGTH)
  }, [filterList])

  const requestParamsWithFilterChunk = useCallback(
    (filterList: string[]): OpalApiRequestParams<TOperationId> => ({
      ...requestParams,
      query: {
        ...requestParams.query,
        filter: {
          ...requestParams.query.filter,
          [filterKey]: filterList,
        },
      },
    }),
    [filterKey, requestParams]
  )

  const results = useQueries({
    queries: filterLists.map((filterChunk) => {
      const params = requestParamsWithFilterChunk(filterChunk)

      return {
        enabled,
        queryKey: [...queryKeyWithoutRequestParams, params],
        queryFn: () => requestFn({ params }),
        select,
      }
    }),
  })

  const flattenedResults = useMemo(
    () => results.flatMap(({ data }) => data),
    [results]
  )

  return flattenedResults
}
